/**
 * forgot-password-page.tsx — Quên mật khẩu (/forgot-password): email → mã 6 số → mật khẩu mới.
 * Server luôn trả cùng một phản hồi (không lộ email có tồn tại). Đặt lại xong: mọi phiên cũ bị đăng xuất, tự đăng nhập
 * (khi bật Cloudflare Turnstile: chuyển về /login có sẵn email — đăng nhập cần xác minh bảo mật).
 * Turnstile: ô xác minh ở bước nhập email, và ở bước nhập mã khi hết thời gian chờ và muốn gửi lại mã.
 */
import { useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'
import { ArrowLeft, Loader2, MailCheck } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { queryClient } from '@/lib/query-client'
import { useAuthStore } from '@/stores/auth-store'
import { captchaPendingMessage } from '@/lib/turnstile'
import { TurnstileBox, useTurnstile } from '@/components/shared/turnstile-box'
import { AUTH_INPUT, AuthLayout, FieldError } from './auth-layout'
import { authApi, toApiErr, useSignupConfig } from './auth-api'
import { isValidEmail, maskEmail, normalizeEmail, passwordError } from './auth-helpers'
import { OtpInput, ResendButton, useCountdown } from './otp-parts'
import { PasswordInput, StrengthMeter } from './password-field'

export function ForgotPasswordPage() {
  const { t } = useTranslation('authPages')
  const [email, setEmail] = useState('')
  const [sentTo, setSentTo] = useState<{ email: string; resendAt: number; message: string } | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { config } = useSignupConfig()
  const siteKey = config?.turnstileSiteKey ?? null
  const ts = useTurnstile(siteKey, 'forgot-password')

  const request = async (e: React.FormEvent) => {
    e.preventDefault()
    const em = normalizeEmail(email)
    if (!isValidEmail(em)) return setError(t('validate.emailInvalid'))
    if (ts.pending) return setError(captchaPendingMessage())
    setBusy(true)
    setError(null)
    try {
      const r = await authApi.forgot(em, ts.token)
      setSentTo({ email: em, resendAt: Date.now() + r.resendInSec * 1000, message: r.message })
    } catch (err) {
      setError(toApiErr(err).message)
      ts.reset() // token Turnstile dùng được một lần
    } finally {
      setBusy(false)
    }
  }

  const footer = <>{t('forgotPassword.rememberPassword')} <Link to="/login" className="font-medium text-primary hover:underline">{t('forgotPassword.login')}</Link></>

  if (sentTo) {
    return (
      <AuthLayout
        title={t('forgotPassword.title')}
        subtitle={<Trans t={t} i18nKey="forgotPassword.sentSubtitle" values={{ email: maskEmail(sentTo.email) }} components={{ b: <b className="text-foreground" /> }} />}
        footer={footer}
      >
        <ResetStep
          email={sentTo.email}
          resendAt={sentTo.resendAt}
          message={sentTo.message}
          siteKey={siteKey}
          onBack={() => setSentTo(null)}
          onResent={(sec) => setSentTo({ ...sentTo, resendAt: Date.now() + sec * 1000 })}
        />
      </AuthLayout>
    )
  }

  return (
    <AuthLayout title={t('forgotPassword.title')} subtitle={t('forgotPassword.subtitle')} footer={footer}>
      <form onSubmit={request} className="space-y-4" noValidate>
        <div className="space-y-1.5">
          <Label htmlFor="fp-email">Email</Label>
          <Input id="fp-email" className={AUTH_INPUT} type="email" autoComplete="username" placeholder="email@congty.vn" value={email} onChange={(e) => { setEmail(e.target.value); setError(null) }} autoFocus />
          <FieldError msg={error} />
        </div>
        <TurnstileBox ts={ts} />
        <Button type="submit" className="h-12 w-full rounded-lg text-base" disabled={busy || ts.pending}>
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} {t('forgotPassword.sendCode')}
        </Button>
      </form>
    </AuthLayout>
  )
}

function ResetStep({ email, resendAt, message, siteKey, onBack, onResent }: {
  email: string
  resendAt: number
  message: string
  /** Khoá công khai Turnstile (null = chưa bật). */
  siteKey: string | null
  onBack: () => void
  onResent: (sec: number) => void
}) {
  const { t } = useTranslation('authPages')
  const navigate = useNavigate()
  const login = useAuthStore((s) => s.login)
  // Ô xác minh chỉ hiện khi đã hết thời gian chờ gửi lại (lúc "Gửi lại mã" bấm được) — gửi lại mã gọi cùng API quên mật khẩu nên cũng cần token.
  const canResend = useCountdown(resendAt) === 0
  const ts = useTurnstile(canResend ? siteKey : null, 'forgot-password')
  const [code, setCode] = useState('')
  const [pw, setPw] = useState('')
  const [pw2, setPw2] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (code.length !== 6) return setError(t('otp.needAllDigits'))
    const pe = passwordError(pw, email)
    if (pe) return setError(pe)
    if (pw !== pw2) return setError(t('validate.confirmPasswordMismatch'))
    setBusy(true)
    setError(null)
    try {
      await authApi.reset(email, code, pw)
      toast.success(t('forgotPassword.resetSuccess'))
      if (siteKey) {
        // Đăng nhập cần xác minh bảo mật (Turnstile) → không tự đăng nhập; về trang đăng nhập có sẵn email.
        navigate('/login', { replace: true, state: { resetEmail: email } })
        return
      }
      try {
        queryClient.removeQueries({ queryKey: ['me', 'permissions'] })
        // Tự đăng nhập ngay sau khi đặt lại mật khẩu (đã xác minh danh tính qua mã gửi email) — không hỏi lại
        // tick đồng ý điều khoản ở bước này, coi như kế tiếp của hành động vừa xác minh.
        await login(email, pw, true, undefined, true)
      } catch (err) {
        toast.error(toApiErr(err).message)
        navigate('/login', { replace: true })
      }
    } catch (err) {
      const a = toApiErr(err)
      setError(a.message)
      if (a.code === 'OTP_LOCKED' || a.code === 'OTP_EXPIRED') setCode('')
    } finally {
      setBusy(false)
    }
  }

  return (
    <form onSubmit={submit} className="space-y-4" noValidate>
      <div className="flex items-start gap-3 rounded-lg bg-primary/5 p-3 text-sm">
        <MailCheck className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
        <span>{message}</span>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="otp">{t('otp.label')}</Label>
        <OtpInput value={code} onChange={(v) => { setCode(v); setError(null) }} disabled={busy} />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="rp-pass">{t('forgotPassword.newPassword')}</Label>
        <PasswordInput id="rp-pass" value={pw} onChange={(e) => setPw(e.target.value)} autoComplete="new-password" maxLength={72} />
        {pw && <StrengthMeter password={pw} />}
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="rp-pass2">{t('forgotPassword.confirmNewPassword')}</Label>
        <PasswordInput id="rp-pass2" value={pw2} onChange={(e) => setPw2(e.target.value)} autoComplete="new-password" maxLength={72} />
      </div>
      {error && <p className="text-sm text-destructive">{error}</p>}
      <Button type="submit" className="h-12 w-full rounded-lg text-base" disabled={busy}>
        {busy && <Loader2 className="h-4 w-4 animate-spin" />} {t('forgotPassword.resetButton')}
      </Button>
      <div className="flex items-center justify-between">
        <button type="button" onClick={onBack} className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> {t('forgotPassword.changeEmail')}
        </button>
        <ResendButton
          until={resendAt}
          disabled={ts.pending}
          onResend={async () => {
            try {
              const r = await authApi.forgot(email, ts.token)
              setCode('')
              onResent(r.resendInSec)
              toast.success(t('forgotPassword.resendSuccess'))
            } catch (err) {
              toast.error(toApiErr(err).message)
              ts.reset()
            }
          }}
        />
      </div>
      <TurnstileBox ts={ts} />
    </form>
  )
}
